import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ActionIcon,
  Button,
  Checkbox,
  Popover,
  SegmentedControl,
  Stack,
  Text
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconColumns3, IconGripVertical } from '@tabler/icons-react';

import { useAppDispatch } from '@/store/hooks';
import {
  useGetUserSettingsQuery,
  useUpdatePagePreferencesMutation
} from '@/store/slices/api/userApiSlice';
import { setFilters as setGlobalFilters } from '@/store/slices/preferencesSlice';
import { useUserSettingsFilters } from '@/hooks/useUserSettingsFilters';

import { PatientListPageColumnPreference } from '@/types/user-preferences';

import { ColumnDef, ColumnId, DEFAULT_COLUMNS, Density } from '../hooks/useColumnPreferences';
import { ENROLLMENT_MODE_COLUMN_IDS } from '../hooks/useRowChecklist';

import styles from './ColumnsButton.module.scss';

interface SortableRowProps {
  column: ColumnDef;
  onVisibilityChange: (id: ColumnId, visible: boolean) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ column, onVisibilityChange }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id
  });

  return (
    <div
      ref={setNodeRef}
      className={styles.row}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 999 : 'auto',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      {...attributes}
      {...listeners}>
      <ActionIcon variant="transparent" color="gray" size="sm" className={styles.dragHandle}>
        <IconGripVertical size={14} />
      </ActionIcon>
      <Checkbox
        checked={column.visible}
        onChange={(e) => onVisibilityChange(column.id, e.currentTarget.checked)}
        label={column.label}
        styles={{ label: { cursor: 'pointer', fontSize: 14 } }}
      />
    </div>
  );
};

interface ColumnsButtonProps {
  columns: ColumnDef[];
  density: Density;
  onVisibilityChange: (id: ColumnId, visible: boolean) => void;
  onReorder: (columns: ColumnDef[]) => void;
  onDensityChange: (density: Density) => void;
}

export const ColumnsButton: React.FC<ColumnsButtonProps> = ({
  columns,
  density,
  onVisibilityChange,
  onReorder,
  onDensityChange
}) => {
  const dispatch = useAppDispatch();
  const { filters: globalFilter } = useUserSettingsFilters();
  const { data: userSettings, refetch: refetchUserSettings } = useGetUserSettingsQuery();
  const [updatePagePreferences, { isLoading: isUpdating }] = useUpdatePagePreferencesMutation();

  const [opened, { open, close }] = useDisclosure(false);
  const isOpenRef = useRef(false);

  // --- Draft state ---
  const [draftColumns, setDraftColumns] = useState<ColumnDef[]>(columns);
  const [draftDensity, setDraftDensity] = useState<Density>(density);
  const [pendingDensity, setPendingDensity] = useState<Density | null>(null);

  const [columnsChangeApplied, setColumnsChangeApplied] = useState(false);

  // Store initial draft state when popover opens to properly compare for changes
  const handleOpen = () => {
    isOpenRef.current = true;
    open();
    setColumnsChangeApplied(false);
  };

  const handleClose = useCallback(() => {
    isOpenRef.current = false;
    close();
    setPendingDensity(null);
    setColumnsChangeApplied(false);
  }, [close]);

  const handleApply = useCallback(async () => {
    try {
      // Construct the column preference object from draft columns
      const columnPreferences: PatientListPageColumnPreference[] = draftColumns.map((col) => ({
        id: col.id,
        visible: col.visible
      }));

      // Determine which density value to use
      const finalDensity = pendingDensity !== null ? pendingDensity : draftDensity;

      await updatePagePreferences({
        settingsId: userSettings?.filterId ?? '',
        patientListPagePreferences: {
          density: finalDensity,
          columns: columnPreferences,
          reportType: userSettings?.patientListPagePreferences?.reportType ?? null,
          patientStatuses: userSettings?.patientListPagePreferences?.patientStatuses ?? []
        }
      }).unwrap();

      // Update global filters with new settings
      dispatch(
        setGlobalFilters({
          clinics: globalFilter?.clinics ?? [],
          practitioners: globalFilter?.practitioners ?? [],
          signingPractitioners: globalFilter?.signingPractitioners ?? [],
          deviceTypes: globalFilter?.deviceTypes ?? [],
          vendors: globalFilter?.vendors ?? [],
          patientStatuses: []
        })
      );

      // Notify parent of density change for proper UI update
      onDensityChange(finalDensity);

      // Mark that columns have been changed and are being applied
      setColumnsChangeApplied(true);

      // Refetch user settings to update column preferences
      await refetchUserSettings();

      // Reset pending state after successful apply
      setPendingDensity(null);

      // Close popover after a short delay to show the changes are being applied
      setTimeout(() => {
        setColumnsChangeApplied(false);
        isOpenRef.current = false;
        close();
      }, 300);
    } catch {
      showNotification({
        title: 'Error',
        message: 'Failed to update column preferences',
        color: 'red'
      });

      // Reset pending state on failure
      setPendingDensity(null);
      setColumnsChangeApplied(false);
    }
  }, [
    draftColumns,
    draftDensity,
    pendingDensity,
    userSettings?.filterId,
    userSettings?.patientListPagePreferences?.patientStatuses,
    updatePagePreferences,
    refetchUserSettings,
    dispatch,
    globalFilter,
    close,
    onDensityChange
  ]);
  const initialColumnsRef = useRef<ColumnDef[]>(columns);
  const initialDensityRef = useRef<Density>(density);

  // Update initial references when popover opens
  useEffect(() => {
    if (opened && userSettings?.patientListPagePreferences) {
      // Initialize draft columns based on user settings if available
      const userColumns = userSettings.patientListPagePreferences.columns || [];

      // Merge user column preferences with default columns
      const mergedColumns = DEFAULT_COLUMNS.map((col) => {
        const userColumn = userColumns.find((uc: any) => uc.id === col.id);
        return {
          ...col,
          visible: userColumn ? userColumn.visible : col.visible
        };
      });

      setDraftColumns(mergedColumns);
      // Handle density properly for string values from user settings
      const densityFromSettings = userSettings.patientListPagePreferences.density;
      setDraftDensity(
        densityFromSettings === 'comfortable' || densityFromSettings === 'compact'
          ? densityFromSettings
          : 'comfortable'
      );

      initialColumnsRef.current = mergedColumns;
      initialDensityRef.current =
        densityFromSettings === 'comfortable' || densityFromSettings === 'compact'
          ? densityFromSettings
          : 'comfortable';
    }
  }, [opened, userSettings]);

  const handleClearAll = useCallback(() => {
    // Reset to default visible state for all columns
    setDraftColumns(columns.map((col) => ({ ...col, visible: true })));
    setDraftDensity('comfortable');
    setPendingDensity(null);
  }, [columns]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = draftColumns.findIndex((c) => c.id === active.id);
      const newIndex = draftColumns.findIndex((c) => c.id === over.id);
      const reordered = arrayMove(draftColumns, oldIndex, newIndex);
      setDraftColumns(reordered);
      onReorder(reordered);
    }
  };

  // Update initial references when popover opens
  useEffect(() => {
    if (opened && userSettings?.patientListPagePreferences) {
      // Initialize draft columns based on user settings if available
      const userColumns = userSettings.patientListPagePreferences.columns || [];

      // Merge user column preferences with default columns
      const mergedColumns = DEFAULT_COLUMNS.map((col) => {
        const userColumn = userColumns.find((uc: any) => uc.id === col.id);
        return {
          ...col,
          visible: userColumn ? userColumn.visible : col.visible
        };
      });

      setDraftColumns(mergedColumns);

      // Handle density properly for string values from user settings
      const densityFromSettings = userSettings.patientListPagePreferences.density;
      setDraftDensity(
        densityFromSettings === 'comfortable' || densityFromSettings === 'compact'
          ? densityFromSettings
          : 'comfortable'
      );

      initialColumnsRef.current = mergedColumns;
      initialDensityRef.current =
        densityFromSettings === 'comfortable' || densityFromSettings === 'compact'
          ? densityFromSettings
          : 'comfortable';
    }
  }, [opened, userSettings]);

  const isApplyDisabled = useMemo(() => {
    // Compare draft state with initial state when popover was opened
    const columnsChanged =
      JSON.stringify(draftColumns) !== JSON.stringify(initialColumnsRef.current);
    const densityChanged =
      (pendingDensity !== null ? pendingDensity : draftDensity) !== initialDensityRef.current;
    return !(columnsChanged || densityChanged);
  }, [draftColumns, draftDensity, pendingDensity]);

  const isClearAllDisabled = useMemo(() => {
    // Check if all columns are already visible and density is comfortable
    const allVisible = draftColumns.every((col) => col.visible);
    const densityIsDefault = draftDensity === 'comfortable';
    return allVisible && densityIsDefault;
  }, [draftColumns, draftDensity]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <Popover
      opened={opened}
      onChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      position="bottom-end"
      shadow="md"
      radius={12}
      offset={6}
      withinPortal>
      <Popover.Target>
        <Button
          variant="default"
          leftSection={<IconColumns3 size={16} />}
          size="sm"
          onClick={opened ? handleClose : handleOpen}>
          Columns
        </Button>
      </Popover.Target>

      <Popover.Dropdown p={0} style={{ minWidth: 260 }}>
        <Stack gap={0}>
          <div className={styles.section}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={4}>
              Show Columns
            </Text>
            <Text size="xs" c="dimmed">
              Drag to reorder
            </Text>
          </div>

          <div className={styles.listSection}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}>
              <SortableContext
                items={draftColumns
                  .filter((col) => !ENROLLMENT_MODE_COLUMN_IDS.includes(col.id))
                  .map((c) => c.id)}
                strategy={verticalListSortingStrategy}>
                {draftColumns
                  .filter((col) => !ENROLLMENT_MODE_COLUMN_IDS.includes(col.id))
                  .map((col) => (
                    <SortableRow
                      key={col.id}
                      column={col}
                      onVisibilityChange={(id, visible) => {
                        setDraftColumns((prev) =>
                          prev.map((c) => (c.id === id ? { ...c, visible } : c))
                        );
                        // Also update the original column state for the component
                        onVisibilityChange(id, visible);
                      }}
                    />
                  ))}
              </SortableContext>
            </DndContext>
          </div>

          <div className={styles.section}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={8}>
              Density
            </Text>
            <SegmentedControl
              fullWidth
              size="xs"
              value={pendingDensity !== null ? pendingDensity : draftDensity}
              onChange={(v) => setPendingDensity(v as Density)}
              data={[
                { value: 'comfortable', label: 'Comfortable' },
                { value: 'compact', label: 'Compact' }
              ]}
            />
          </div>

          <div className={styles.footer}>
            <Button
              variant="transparent"
              color="plum"
              size="xs"
              disabled={isClearAllDisabled}
              onClick={handleClearAll}>
              Clear All
            </Button>
            <Button.Group>
              <Button variant="default" size="xs" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="xs"
                color="plum"
                loading={isUpdating || columnsChangeApplied}
                disabled={isApplyDisabled}
                onClick={handleApply}>
                Apply
              </Button>
            </Button.Group>
          </div>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
